import React from 'react';
import { Sparkles, ArrowRight, CircleHelp } from 'lucide-react';
import { useSearchDebounce } from '../../../hooks/useSearchDebounce';

interface AudienceSearchProps {
    onSearch: (query: string) => void;
    resultCount?: number;
    loading?: boolean;
    showTutorial?: boolean;
}

const AudienceSearch = ({ onSearch, loading = false, showTutorial }: AudienceSearchProps) => {
    // Custom hook: debounce + Enter inmediato.
    // Audit A-02: bajamos el debounce de 3000ms a 800ms (3s se sentía colgado).
    const {
        inputValue,
        handleInputChange,
        handleKeyDown,
        triggerSearchNow
    } = useSearchDebounce({
        onSearch,
        delay: 800
    });

    return (
        <div className="relative group">
            {/* Tutorial Overlay */}
            {showTutorial && (
                <div className="absolute inset-0 bg-violet-900/90 z-50 rounded-xl flex items-center justify-center p-4 text-center animate-fade-in backdrop-blur-sm">
                    <div className="text-white flex items-center gap-3">
                        <CircleHelp className="w-5 h-5 text-violet-300 shrink-0" />
                        <p className="text-sm font-medium leading-relaxed">
                            Escribe consultas naturales como "clientes Premium" o "quienes no compran hace 30 días". Mi IA segmentará la base de datos por ti.
                        </p>
                    </div>
                </div>
            )}
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-200 to-indigo-200 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>

            <div className="relative bg-white border border-slate-200 rounded-xl flex items-center p-1.5 shadow-sm transition-all group-hover:shadow-md group-hover:border-violet-200">
                <div className="p-3 text-violet-500 animate-pulse">
                    <Sparkles size={20} />
                </div>
                <input
                    type="text"
                    aria-label="Buscar o segmentar tu audiencia con IA"
                    placeholder="Buscá o segmentá tu audiencia con IA — ej.: «VIP que no compran hace 30 días»"
                    className="w-full bg-transparent border-none text-slate-900 placeholder-slate-400 focus:ring-0 focus:outline-none px-2 py-2 text-sm"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                />
                <button
                    onClick={triggerSearchNow}
                    disabled={loading}
                    className="px-6 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 min-w-[140px] whitespace-nowrap disabled:opacity-70"
                >
                    {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                        <>
                            Buscar con IA
                            <ArrowRight size={14} className="opacity-70" />
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default AudienceSearch;
